// Phase 1 (iter1): AppShell (Header + 3 列構造、Sub-sidebar + Detail 両側 collapsible / drag resize)
//
// ┌──────────────────────────────────────────────────┐
// │ AppHeader (global search / counts / controls)    │
// ├──┬─────────────┬─┬──────────────┬─┬──────────────┤
// │⌂│ Sub-sidebar │↕│ Main surface │↕│ Detail        │
// │..│ (lens機能)  │ │              │ │               │
// └──┴─────────────┴─┴──────────────┴─┴──────────────┘
//                  ↑ drag handle    ↑ drag handle

import type { ReactNode } from 'react'

interface AppShellProps {
  header?: ReactNode
  lensNav: ReactNode
  subSidebar: ReactNode
  main: ReactNode
  detail?: ReactNode
  detailVisible: boolean
  detailWidth?: number
  onDetailResizeStart?: (e: React.MouseEvent) => void
  subSidebarWidth?: number
  onSubSidebarResizeStart?: (e: React.MouseEvent) => void
}

export function AppShell({
  header,
  lensNav,
  subSidebar,
  main,
  detail,
  detailVisible,
  detailWidth = 400,
  onDetailResizeStart,
  subSidebarWidth = 288,
  onSubSidebarResizeStart,
}: AppShellProps) {
  const hasSubSidebar = subSidebar != null

  return (
    <div className="h-screen w-screen flex flex-col bg-bg-base text-text-base overflow-hidden">
      {/* Header (optional) */}
      {header && <div className="flex-shrink-0">{header}</div>}

      {/* 3 列 layout */}
      <div className="flex-1 flex overflow-hidden min-h-0">
        {/* 左端 Lens nav */}
        <nav
          aria-label="Lens navigation"
          className="w-16 flex-shrink-0 border-r border-border-subtle bg-bg-base flex flex-col items-center py-4 gap-2"
        >
          {lensNav}
        </nav>

        {/* Sub-sidebar (+ drag handle) */}
        {hasSubSidebar && (
          <>
            <aside
              aria-label="Lens sidebar"
              className="flex-shrink-0 border-r border-border-subtle bg-bg-surface overflow-y-auto"
              style={{ width: subSidebarWidth }}
            >
              {subSidebar}
            </aside>
            <div
              role="separator"
              aria-orientation="vertical"
              aria-label="Resize sidebar"
              onMouseDown={onSubSidebarResizeStart}
              className="w-1.5 flex-shrink-0 hover:bg-accent-primary/40 cursor-col-resize transition-colors group"
            >
              <div className="w-px h-full mx-auto bg-border-subtle group-hover:bg-accent-primary/60 transition-colors" />
            </div>
          </>
        )}

        {/* Main + Detail */}
        <div className="flex-1 flex overflow-hidden min-w-0">
          <section aria-label="Main surface" className="flex-1 overflow-y-auto bg-bg-base min-w-0">
            {main}
          </section>

          {detailVisible && detail && (
            <>
              {/* Drag handle */}
              <div
                role="separator"
                aria-orientation="vertical"
                aria-label="Resize detail panel"
                onMouseDown={onDetailResizeStart}
                className="w-1.5 flex-shrink-0 hover:bg-accent-primary/40 cursor-col-resize transition-colors group"
              >
                <div className="w-px h-full mx-auto bg-border-subtle group-hover:bg-accent-primary/60 transition-colors" />
              </div>

              {/* Detail panel */}
              <aside
                aria-label="Detail panel"
                className="flex-shrink-0 border-l border-border-subtle bg-bg-surface overflow-y-auto"
                style={{ width: detailWidth }}
              >
                {detail}
              </aside>
            </>
          )}
        </div>
      </div>
    </div>
  )
}
